import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Boxes, Coins, Gift, LayoutDashboard, LogOut, User, Users, Wallet,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const NAV = [
  { to: "/app", end: true, label: "Overview", icon: LayoutDashboard },
  { to: "/app/wallet", label: "Wallet", icon: Wallet },
  { to: "/app/token", label: "WIN Token", icon: Coins },
  { to: "/app/products", label: "Products", icon: Boxes },
  { to: "/app/rewards", label: "Rewards", icon: Gift },
  { to: "/app/referrals", label: "Referrals", icon: Users },
  { to: "/app/profile", label: "Profile", icon: User },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors duration-300 ${
    isActive ? "bg-[#D4AF37]/10 text-[#F3E5AB] border border-[#D4AF37]/30" : "text-[#99A1AF] border border-transparent hover:text-white hover:bg-white/5"
  }`;

export default function MembersLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-white" data-testid="members-area">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-white/[0.08] bg-[#0B0B0F] lg:flex">
        <div className="flex items-center gap-3 px-6 py-6">
          <img src="/winter-logo.jpg" alt="Winter Protocol" className="h-9 w-9 rounded-lg object-cover mix-blend-screen" />
          <div className="leading-none">
            <span className="block font-display text-xs font-bold tracking-[0.18em]">WINTER PROTOCOL</span>
            <span className="mt-1 block font-mono text-[9px] tracking-[0.25em] text-[#636B78] uppercase">Members Area</span>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-4 py-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`members-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/[0.08] p-4">
          <p className="truncate px-2 pb-3 text-xs text-[#636B78]" data-testid="members-user-email">{user?.email}</p>
          <button
            onClick={onLogout}
            data-testid="members-logout-button"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-[#99A1AF] transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-white/[0.08] bg-[#08080A]/90 backdrop-blur-xl lg:hidden">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-2.5">
              <img src="/winter-logo.jpg" alt="Winter Protocol" className="h-8 w-8 rounded-lg object-cover mix-blend-screen" />
              <span className="font-display text-xs font-bold tracking-[0.18em]">WINTER PROTOCOL</span>
            </div>
            <button onClick={onLogout} data-testid="members-logout-button-mobile" className="p-2 text-[#99A1AF] hover:text-white" aria-label="Logout">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-4 pb-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`members-nav-mobile-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                    isActive ? "bg-[#D4AF37]/15 text-[#F3E5AB]" : "text-[#99A1AF] hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8 sm:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
